import { useState, useEffect } from 'react'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Dialog from './Dialog'
import InputField from './InputField'
import GridCheckbox from './GridCheckbox'
import GridIconButton from './GridIconButton'
import GridAutoComplete from './GridAutoComplete'
import SortableTableHead from './SortableTableHead'
import GridCategoryRequirement from './GridCategoryRequirement'
import GridCategoryRequirements from './GridCategoryRequirements'
import {
  formatBool,
  formatEuro,
  formatBodyField,
} from '../hooks/utils-functions'
import { colours, titlesImpostazioni, TESSERE } from '../settings/settings'
import {
  useGetCategorieQuery,
  useCreateCategoriaMutation,
  useUpdateCategoriaMutation,
} from '../hooks/api-slice'

const TESSERE_PLAYERS = TESSERE?.filter(t => t.group === 'players')?.map(
  t => t.key,
)

const columnsCategorie = [
  { label: '', sortable: false },
  { label: 'Categoria', sortable: false },
  { label: 'Costo', sortable: false },
  { label: 'Mini', sortable: false },
  { label: 'Propaganda', sortable: false },
  { label: 'Libero', sortable: false },
  { label: 'Tessere', sortable: false },
  { label: 'Requisiti', sortable: false },
  { label: 'Arbitraggio', sortable: false },
  { label: 'Attiva', sortable: false },
  { label: '', sortable: false },
]

const DialogCategorie = ({ open, handleClose }) => {
  const { data: categorie } = useGetCategorieQuery()
  const [createCategoriaMutation] = useCreateCategoriaMutation()
  const [updateCategoriaMutation] = useUpdateCategoriaMutation()

  const [editingCategoria, setEditingCategoria] = useState()
  const [addingCategoria, setAddingCategoria] = useState()
  const [newCategoria, setNewCategoria] = useState()

  const cSetNewCategoria = (field, value) =>
    setNewCategoria({ ...newCategoria, [field]: formatBodyField(value) })

  const clearNewCategoria = () => {
    setEditingCategoria()
    setAddingCategoria()
    setNewCategoria()
  }

  useEffect(() => {
    clearNewCategoria()
  }, [open])

  const addNewCategoria = async () => {
    const result = await createCategoriaMutation(newCategoria)
    if (!result?.error) clearNewCategoria()
  }

  const editCategoria = async id => {
    const result = await updateCategoriaMutation({ id, ...newCategoria })
    if (!result?.error) clearNewCategoria()
  }

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleCategorie}
      stretchWidth
      stretchHeight
    >
      {!categorie ? (
        <h4>Categorie non trovate.</h4>
      ) : (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid container alignItems='center' sx={{ width: '100%' }}>
            {(!!addingCategoria && (
              <>
                <Grid size={{ xs: 12, md: 3 }}>
                  <center>
                    <InputField
                      id='categoria'
                      value={newCategoria?.categoria}
                      onChange={i => cSetNewCategoria('categoria', i)}
                      autoFocus
                      label={columnsCategorie[0].label}
                    />
                  </center>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <center>
                    <InputField
                      id='nome'
                      value={newCategoria?.nome}
                      onChange={i => cSetNewCategoria('nome', i)}
                      label={columnsCategorie[1].label}
                    />
                  </center>
                </Grid>
                <GridIconButton
                  type='save'
                  size={{ xs: 6, md: 'grow' }}
                  onClick={addNewCategoria}
                />
                <GridIconButton
                  type='clear'
                  size={{ xs: 6, md: 'grow' }}
                  onClick={clearNewCategoria}
                />
              </>
            )) || (
              <GridIconButton
                type='add'
                size={12}
                onClick={() => {
                  setEditingCategoria()
                  setAddingCategoria(true)
                  setNewCategoria({})
                }}
                text='Nuova categoria'
              />
            )}
          </Grid>
          {(categorie.length === 0 && <h4>Nessuna categoria trovata.</h4>) || (
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={categorie}
                  columns={columnsCategorie}
                />
                <TableBody>
                  {categorie.map((c, index) => {
                    return (
                      <TableRow
                        key={index}
                        style={{ backgroundColor: colours.white }}
                      >
                        {(!!editingCategoria &&
                          newCategoria?.categoria === c.categoria && (
                            <>
                              <TableCell padding='checkbox'>
                                {c.categoria}
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <InputField
                                  id='descrizione'
                                  value={newCategoria?.nome}
                                  onChange={i => cSetNewCategoria('nome', i)}
                                  autoFocus
                                  label={columnsCategorie[1].label}
                                  mode='update'
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <InputField
                                  id='costoIscrizione'
                                  label={columnsCategorie[2].label}
                                  value={newCategoria?.costoIscrizione}
                                  onChange={i =>
                                    cSetNewCategoria('costoIscrizione', i)
                                  }
                                  type='number'
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridCheckbox
                                  value={!!newCategoria?.isMini}
                                  onChange={(_, x) =>
                                    cSetNewCategoria('isMini', x)
                                  }
                                  md={12}
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridCheckbox
                                  value={!!newCategoria?.isPropaganda}
                                  onChange={(_, x) =>
                                    cSetNewCategoria('isPropaganda', x)
                                  }
                                  md={12}
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridCheckbox
                                  value={!!newCategoria?.hasLibero}
                                  onChange={(_, x) =>
                                    cSetNewCategoria('hasLibero', x)
                                  }
                                  md={12}
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridAutoComplete
                                  label='Tessere'
                                  field='tesseratiRoles'
                                  value={newCategoria?.tesseratiRoles}
                                  options={TESSERE_PLAYERS}
                                  onChange={cSetNewCategoria}
                                  md={12}
                                  multiple
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                {newCategoria?.requirements?.map((x, index) => (
                                  <GridCategoryRequirements
                                    key={index}
                                    index={index}
                                    x={x}
                                    values={newCategoria?.requirements}
                                    onChange={x =>
                                      cSetNewCategoria('requirements', x)
                                    }
                                    readOnly={false}
                                  />
                                ))}
                                <GridCategoryRequirement
                                  values={newCategoria?.requirements}
                                  onChange={x =>
                                    cSetNewCategoria('requirements', x)
                                  }
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridCheckbox
                                  value={!!newCategoria?.needsReferee}
                                  onChange={(_, x) =>
                                    cSetNewCategoria('needsReferee', x)
                                  }
                                  md={12}
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <GridCheckbox
                                  value={!!newCategoria?.isActive}
                                  onChange={(_, x) =>
                                    cSetNewCategoria('isActive', x)
                                  }
                                  md={12}
                                />
                              </TableCell>
                              <TableCell padding='checkbox'>
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{ width: '100%' }}
                                >
                                  <GridIconButton
                                    type='save'
                                    onClick={() => editCategoria(c.id)}
                                  />
                                  <GridIconButton
                                    type='clear'
                                    onClick={clearNewCategoria}
                                  />
                                </Grid>
                              </TableCell>
                            </>
                          )) || (
                          <>
                            <TableCell>{c.categoria}</TableCell>
                            <TableCell>{c.nome}</TableCell>
                            <TableCell>
                              {formatEuro(c.costoIscrizione)}
                            </TableCell>
                            <TableCell>{formatBool(c.isMini)}</TableCell>
                            <TableCell>{formatBool(c.isPropaganda)}</TableCell>
                            <TableCell>{formatBool(c.hasLibero)}</TableCell>
                            <TableCell>
                              {c.tesseratiRoles?.join(', ')}
                            </TableCell>
                            <TableCell>
                              {c.requirements?.map((x, index) => (
                                <GridCategoryRequirements
                                  key={index}
                                  index={index}
                                  x={x}
                                />
                              ))}
                            </TableCell>
                            <TableCell>{formatBool(c.needsReferee)}</TableCell>
                            <TableCell>{formatBool(c.isActive)}</TableCell>
                            <TableCell>
                              <center>
                                <GridIconButton
                                  type='edit'
                                  size={6}
                                  onClick={() => {
                                    setEditingCategoria(true)
                                    setAddingCategoria(false)
                                    setNewCategoria(c)
                                  }}
                                />
                              </center>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      )}
    </Dialog>
  )
}

export default DialogCategorie
